import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { SafeSEO } from '@/components/seo/SafeSEO';
import { SEO_ROUTES } from '@/seo/seo-routes';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Card } from '@/components/ui/card';
import ShieldCheckIcon from 'lucide-react/dist/esm/icons/shield-check';
import FileCheck2Icon from 'lucide-react/dist/esm/icons/file-check-2';
import ServerIcon from 'lucide-react/dist/esm/icons/server';
import RadarIcon from 'lucide-react/dist/esm/icons/radar';
import GlobeIcon from 'lucide-react/dist/esm/icons/globe';
import BellRingIcon from 'lucide-react/dist/esm/icons/bell-ring';
import { faqs } from '@/data/faq';

const certificateUrl =
  'https://www.bsigroup.com/en-GB/products-and-services/assessment-and-certification/validation-and-verification/client-directory-profile/LANEWA-0047771987-000';
const lanewayRecertificationUrl =
  'https://www.lanewayanalytics.com.au/insights/iso-27001-recertification';

const securityFaqs = faqs
  .filter((faq) => faq.topics.includes('security'))
  .slice(0, 4);

const Security: React.FC = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  return (
    <>
      <SafeSEO
        title="Security at Joey"
        description="How we protect your data while delivering AI Smart Alerts for bullying, risky contacts, money asks and safety risks."
        url={SEO_ROUTES.security}
        image="/img/og/joey-default-1200x630.png"
      />
      <div className="min-h-screen bg-background">
        <Navigation />

        <main className="container px-6 md:px-10 lg:px-16 xl:px-20 pt-32 md:pt-36 lg:pt-40 pb-24 md:pb-28 lg:pb-32">
          <header className="max-w-4xl space-y-6">
            <p className="uppercase tracking-[0.3em] text-sm text-muted-foreground">Security & Trust</p>
            <h1 className="text-4xl md:text-5xl font-semibold text-joey-navy leading-tight">
              Joey is built to be the safest place to protect your child's digital life
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
              Laneway Analytics Pty Ltd operates Joey under ISO/IEC 27001:2022 certification. Every control is audited annually (by{' '}
              <a
                href="https://www.bsigroup.com/en-GB/products-and-services/standards/iso-iec-27001-information-security-management-system/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-joey-navy underline underline-offset-4 hover:text-joey-navy/80"
              >
                BSI
              </a>
              ), data stays in AWS, and privacy-by-design workflows keep families in control while meeting GDPR expectations for EU and
              UK parents.
            </p>
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <a
                href={certificateUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center rounded-full bg-joey-navy px-8 py-3 text-base font-medium text-white shadow-sm transition hover:bg-joey-navy/90"
              >
                View ISO/IEC 27001 certificate
              </a>
              <Link
                to="/get-started"
                className="inline-flex items-center justify-center rounded-full border border-joey-navy px-8 py-3 text-base font-medium text-joey-navy transition hover:bg-joey-navy/10"
              >
                Talk to us about security
              </Link>
            </div>
          </header>

          <section className="mt-20 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            <Card className="h-full border border-joey-navy/20 bg-white/70 p-8">
              <div className="flex items-center gap-4 mb-4">
                <span className="rounded-full bg-joey-navy/10 p-3 text-joey-navy">
                  <ShieldCheckIcon className="h-6 w-6" />
                </span>
                <h2 className="text-xl font-semibold text-joey-navy">Certified controls</h2>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                ISO/IEC 27001:2022 certification (certificate number <strong>IS 728964</strong>) confirms that our information security
                management system covers every Joey product, employee, and supporting vendor. BSI performs the certification audit and
                yearly surveillance reviews.
              </p>
            </Card>

            <Card className="h-full border border-joey-navy/20 bg-white/70 p-8">
              <div className="flex items-center gap-4 mb-4">
                <span className="rounded-full bg-joey-navy/10 p-3 text-joey-navy">
                  <FileCheck2Icon className="h-6 w-6" />
                </span>
                <h2 className="text-xl font-semibold text-joey-navy">Independent assurance</h2>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                BSI conducts annual ISO audits, and we commission third-party penetration tests at least once per year, after major
                releases, or whenever risk dictates. Findings feed directly into our remediation and continuous improvement cycles.
              </p>
            </Card>

            <Card className="h-full border border-joey-navy/20 bg-white/70 p-8">
              <div className="flex items-center gap-4 mb-4">
                <span className="rounded-full bg-joey-navy/10 p-3 text-joey-navy">
                  <ServerIcon className="h-6 w-6" />
                </span>
                <h2 className="text-xl font-semibold text-joey-navy">AWS-grade security</h2>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                Joey runs on Amazon Web Services with defence-in-depth controls-encryption at rest and in transit, multi-region redundancy, automated patching, and best-practice identity management-so child data inherits the same safeguards trusted by leading security-conscious companies worldwide.
              </p>
            </Card>

            <Card className="h-full border border-joey-navy/20 bg-white/70 p-8">
              <div className="flex items-center gap-4 mb-4">
                <span className="rounded-full bg-joey-navy/10 p-3 text-joey-navy">
                  <GlobeIcon className="h-6 w-6" />
                </span>
                <h2 className="text-xl font-semibold text-joey-navy">GDPR ready</h2>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                Parents across the EU and UK can rely on GDPR-compliant consent flows, data minimisation, and export/deletion
                workflows. ISO controls, role-based access, and audit logging protect personal data even while hosted in Australia.
              </p>
            </Card>

            <Card className="h-full border border-joey-navy/20 bg-white/70 p-8">
              <div className="flex items-center gap-4 mb-4">
                <span className="rounded-full bg-joey-navy/10 p-3 text-joey-navy">
                  <RadarIcon className="h-6 w-6" />
                </span>
                <h2 className="text-xl font-semibold text-joey-navy">Continuous monitoring</h2>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                Least-privilege access, 2FA enforced through Auth0, automated alerting, and detailed audit trails give us real-time
                visibility. Security reviews are embedded into every release and backed by change management controls.
              </p>
            </Card>

            <Card className="h-full border border-joey-navy/20 bg-white/70 p-8">
              <div className="flex items-center gap-4 mb-4">
                <span className="rounded-full bg-joey-navy/10 p-3 text-joey-navy">
                  <BellRingIcon className="h-6 w-6" />
                </span>
                <h2 className="text-xl font-semibold text-joey-navy">Transparent incident response</h2>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                We notify parents without delay if a breach could impact their family. Our plan aligns with the Australian Notifiable
                Data Breaches scheme and GDPR/UK GDPR 72-hour timelines, and includes direct outreach plus guidance from our Safety
                Desk.
              </p>
            </Card>
          </section>

          <section className="mt-24 grid gap-10 lg:grid-cols-[1.4fr_1fr]">
            <Card className="border border-joey-navy/20 bg-white/80 p-10">
              <h2 className="text-3xl font-semibold text-joey-navy mb-6">How to verify our ISO certificate</h2>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                ISO/IEC 27001 is the globally recognised benchmark for information security management. Use the steps below to confirm
                Joey's standing any time you need procurement assurance or personal peace of mind.
              </p>
              <ol className="list-decimal list-inside space-y-4 text-muted-foreground">
                <li>
                  Open the BSI client directory listing for Laneway Analytics Pty Ltd (certificate number <strong>IS 728964</strong>).
                </li>
                <li>
                  Confirm the standard shown is ISO/IEC 27001:2022 and that the status is "Active'" with the current validity dates.
                </li>
                <li>
                  Download the PDF certificate or share the public URL with your school or co-guardians as part of due diligence.
                </li>
              </ol>
              <p className="text-muted-foreground mt-6 leading-relaxed">
                Want extra assurance? Read Laneway Analytics' announcement about recertifying to the latest ISO/IEC 27001 standard to understand how we keep controls current and in line with global best practice.
              </p>
              <a
                href={lanewayRecertificationUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex w-fit items-center justify-center rounded-full border border-joey-navy px-6 py-3 text-sm font-medium text-joey-navy transition hover:bg-joey-navy/10"
              >
                Read Laneway's ISO 27001 recertification update
              </a>
              <a
                href={certificateUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex w-fit items-center justify-center rounded-full border border-joey-navy px-6 py-3 text-sm font-medium text-joey-navy transition hover:bg-joey-navy/10"
              >
                Visit the BSI directory listing
              </a>
            </Card>

            <Card className="border border-joey-navy/10 bg-joey-navy text-white p-10">
              <h3 className="text-2xl font-semibold mb-4">Need a security package?</h3>
              <p className="text-white/90 leading-relaxed">
                Our team can provide ISO audit letters, penetration test summaries, and data flow diagrams under NDA. Reach out to
                schedule a walkthrough with our CISO or Security team.
              </p>
              <Link
                to="/contact"
                className="mt-6 inline-flex items-center justify-center rounded-full bg-white px-6 py-3 text-sm font-semibold text-joey-navy transition hover:bg-white/90"
              >
                Contact security
              </Link>
            </Card>
          </section>

          <section className="mt-24">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
              <div>
                <h2 className="text-3xl font-semibold text-joey-navy">Security FAQs</h2>
                <p className="text-muted-foreground mt-2 max-w-2xl">
                  Parents often ask about our audits, data residency, and international compliance. Here are a few highlights-you can
                  explore the full list on our FAQ page.
                </p>
              </div>
              <Link
                to="/faqs"
                className="inline-flex items-center justify-center rounded-full border border-joey-navy px-5 py-2 text-sm font-medium text-joey-navy transition hover:bg-joey-navy/10"
              >
                View all FAQs
              </Link>
            </div>

            <div className="mt-10 grid gap-6 md:grid-cols-2">
              {securityFaqs.map((faq) => (
                <Card key={faq.id} className="border border-joey-navy/15 bg-white/70 p-8">
                  <h3 className="text-xl font-semibold text-joey-navy mb-3">{faq.question}</h3>
                  <p className="text-muted-foreground leading-relaxed">{faq.answer}</p>
                </Card>
              ))}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default Security;

